import { useParams } from "react-router-dom";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { Page, PageContent, PageFooter } from "@/shared/components/ui/Page";
import { PageHeader, PageHeaderAction } from "@/shared/components/ui/PageHeader";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { MessageInput } from "../components/MessageInput";
import { MessagesList } from "../components/MessagesList";
import { useConversation } from "../hooks/conversationQueries";
import { useCreateMessage, useMessages } from "../hooks/messageQueries";
import { usePhoneNumbers } from "../hooks/phoneNumberQueries";
import { useMessageDraft } from "../hooks/useMessageDraft";
import "../components/messages.css";

export function MessagesListPage() {
  const { businessId, business, loading } = useCurrentBusiness();
  const { conversationId } = useParams<{ conversationId: string }>();
  const conversationQuery = useConversation(conversationId);
  const conversation = conversationQuery.data;
  const phoneQuery = usePhoneNumbers();
  const phoneNumber = phoneQuery.data?.find((phone) => phone.id === conversation?.phoneNumberId);
  const messagesQuery = useMessages({
    conversationId,
    phoneNumberId: conversation?.phoneNumberId,
  }, !!conversationId && !!conversation);
  const createMessage = useCreateMessage();
  const { draft, setText, send } = useMessageDraft(businessId, conversationId);

  const sortedMessages = (messagesQuery.data || [])
    .filter((message) => message.conversationId === conversationId && !message.deletedAt)
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime() || a.id.localeCompare(b.id));
  const customerName = [conversation?.client?.firstName, conversation?.client?.lastName]
    .filter(Boolean).join(" ").trim() || conversation?.externalNumber || "Unknown customer";
  const isLoading = loading || conversationQuery.isLoading || messagesQuery.isLoading;
  const error = conversationQuery.error || messagesQuery.error;
  const refreshing = conversationQuery.isFetching || phoneQuery.isFetching || messagesQuery.isFetching;
  const refresh = () => {
    if (!businessId) return;
    if (conversationId) void conversationQuery.refetch();
    void phoneQuery.refetch();
    if (conversation) void messagesQuery.refetch();
  };

  const unavailableReason = !businessId || !conversationId || !conversation
    ? "Select an existing conversation to send a message."
    : isLoading ? "Wait for the conversation to load before sending."
    : error ? "Refresh the conversation before sending. Your draft is kept."
    : phoneQuery.isLoading ? "Checking the conversation's sending number..."
    : phoneQuery.error ? "Couldn't verify the sending number. Refresh to try again."
    : !phoneNumber || phoneNumber.status !== "active" || !phoneNumber.number.trim() || !phoneNumber.twilioSid.trim()
      ? "Sending is unavailable: this conversation's phone number is not active or configured. Your draft is kept."
    : !conversation.externalNumber.trim() ? "Sending is unavailable: this conversation has no recipient phone number."
    : undefined;

  const handleSend = () => {
    if (unavailableReason || !conversation || !phoneNumber || !businessId) return;
    void send({
      conversationId: conversation.id,
      phoneNumberId: phoneNumber.id,
      businessId,
      body: draft.text.trim(),
      toNumber: conversation.externalNumber,
      fromNumber: phoneNumber.number,
      direction: "outbound",
    }, async (intent) => {
      const message = await createMessage.mutateAsync(intent);
      if (message.status === "failed") throw new Error("The message could not be sent.");
    });
  };

  return (
    <Page>
      <PageHeader
        title={conversation ? customerName : "Messages"}
        actions={
          <PageHeaderAction onClick={refresh} disabled={!businessId || refreshing}>
            {refreshing ? "Refreshing..." : "Refresh"}
          </PageHeaderAction>
        }
      />
      <PageContent>
        {!conversationId ? <EmptyState title="No conversation selected" message="Open an existing customer conversation from Messages." /> :
          error ? <ErrorState title="Couldn't load messages" message={error.message} onRetry={refresh} /> :
          isLoading ? <LoadingSpinner message="Loading messages..." /> :
          !conversation ? <EmptyState title="Conversation not found" message="This conversation is not available in this business." /> :
          sortedMessages.length === 0 ? <EmptyState title="No messages yet" message="Messages in this conversation will appear here." /> :
          <MessagesList
            key={`${businessId}:${conversationId}`}
            messages={sortedMessages}
            customerName={customerName}
            timeZone={business?.timezone || "UTC"}
          />
        }
      </PageContent>
      <PageFooter>
        <MessageInput
          key={`${businessId}:${conversationId}`}
          value={draft.text}
          onChange={setText}
          onSend={handleSend}
          disabled={!!unavailableReason || draft.blocked}
          pending={draft.pending}
          retrying={!!draft.intent}
          readOnly={!!draft.intent || draft.blocked}
          error={draft.error}
          unavailableReason={unavailableReason}
        />
      </PageFooter>
    </Page>
  );
}
